import { useEffect, useMemo, useState } from 'react';
import {
  usePermissionsAdministration,
  type CapabilityOption,
  type PolicyDiffItem,
  type PolicyRevision,
  type PolicyScope,
  type RoleGrantDiffItem,
  type ValidationError,
} from '../hooks/usePermissionsAdministration';
import Spinner from './Spinner';
import RelationshipRoleGrants from './RelationshipRoleGrants';
import {
  ENFORCEMENT_LABELS,
  permissionScopeLabel,
  policyIntentNotice,
  roleGrantKeys,
  withRoleGrant,
} from '../lib/permissionsPresentation';

const CATEGORY_LABELS: Record<string, string> = {
  actions: 'Actions', advancement: 'Advancement', communications: 'Communications', contacts: 'Contacts',
  fos: 'FOS', initiatives: 'Initiatives', renewal: 'Renewal', tasks: 'Tasks', training: 'Training',
  unitHealth: 'Unit Health', commissioners: 'Commissioners',
  unitMap: 'Unit Map',
  userAccess: 'Login & User Access',
};
const CATEGORY_ORDER = [
  'actions', 'advancement', 'communications', 'contacts', 'fos', 'initiatives',
  'renewal', 'tasks', 'training', 'unitMap', 'unitHealth', 'commissioners', 'userAccess',
] as const;
type DisplayCategory = (typeof CATEGORY_ORDER)[number];
const HIDDEN_POSITION_MATRIX_DOMAINS = new Set(['dataImports', 'goals', 'strategy']);
const HIDDEN_POSITION_MATRIX_CAPABILITIES = new Set(['units.recomputeHealth']);
const COMMISSIONER_CAPABILITIES = new Set([
  'units.manageCommissionerAssignments',
  'units.viewCommissionerCoverage',
]);

function displayCategory(capability: CapabilityOption): DisplayCategory {
  if (capability.domain === 'units') {
    return COMMISSIONER_CAPABILITIES.has(capability.key) ? 'commissioners' : 'unitHealth';
  }
  return capability.domain as DisplayCategory;
}

function cloneRevision(revision: PolicyRevision): PolicyRevision {
  return JSON.parse(JSON.stringify(revision)) as PolicyRevision;
}

export default function PermissionsMatrix() {
  const api = usePermissionsAdministration();
  const [view, setView] = useState<'active' | 'draft' | 'history'>('active');
  const [selectedCategory, setSelectedCategory] = useState<DisplayCategory>('actions');
  const [working, setWorking] = useState<PolicyRevision | null>(null);
  const [positionSearch, setPositionSearch] = useState('');
  const [capabilitySearch, setCapabilitySearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [review, setReview] = useState<{ diff: PolicyDiffItem[]; roleDiff: RoleGrantDiffItem[]; fingerprint: string | null } | null>(null);

  useEffect(() => {
    setWorking(api.data?.draft ? cloneRevision(api.data.draft) : null);
  }, [api.data?.draft]);

  const dirty = !!working && !!api.data?.draft && JSON.stringify(working) !== JSON.stringify(api.data.draft);
  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);

  const matrixCapabilities = useMemo(() => (api.data?.capabilities ?? [])
    .filter((capability) =>
      !HIDDEN_POSITION_MATRIX_DOMAINS.has(capability.domain)
      && !HIDDEN_POSITION_MATRIX_CAPABILITIES.has(capability.key)), [api.data?.capabilities]);
  const groups = useMemo(() => CATEGORY_ORDER.filter((category) =>
    matrixCapabilities.some((capability) => displayCategory(capability) === category)), [matrixCapabilities]);
  useEffect(() => {
    if (groups.length > 0 && !groups.includes(selectedCategory)) {
      setSelectedCategory(groups[0]);
    }
  }, [groups, selectedCategory]);
  const capabilities = useMemo(() => matrixCapabilities.filter((capability) => {
    if (displayCategory(capability) !== selectedCategory) return false;
    const q = capabilitySearch.trim().toLowerCase();
    return !q || `${capability.label} ${capability.description}`.toLowerCase().includes(q);
  }), [matrixCapabilities, selectedCategory, capabilitySearch]);
  const positions = useMemo(() => (api.data?.positions ?? []).filter((position) => {
    const q = positionSearch.trim().toLowerCase();
    return !q || position.displayName.toLowerCase().includes(q);
  }), [api.data?.positions, positionSearch]);

  if (api.loading) return (
    <section className="card set-card permissions-admin">
      <div className="permissions-loading">
        <Spinner label="Loading Permissions policy…" size={32} />
      </div>
    </section>
  );
  if (api.error || !api.data) return <section className="card set-card"><p className="err">{api.error || 'Permissions policy unavailable.'}</p></section>;
  const administration = api.data;

  const shown = view === 'draft' && working ? working : api.data.active;
  const grantsFor = (positionId: string) => shown.policy.entries.find((entry) => entry.positionId === positionId)?.grants ?? [];
  const allowedScopes = (positionLevel: 'district' | 'council', capabilityScopes: PolicyScope[]) =>
    capabilityScopes.filter((scope) => positionLevel === 'council' || scope !== 'council');
  const changeGrant = (positionId: string, capabilityKey: string, checked: boolean, scope: PolicyScope) => {
    if (!working) return;
    const next = cloneRevision(working);
    let entry = next.policy.entries.find((item) => item.positionId === positionId);
    if (!entry) { entry = { positionId, grants: [] }; next.policy.entries.push(entry); }
    entry.grants = checked
      ? [...entry.grants.filter((grant) => grant.capabilityKey !== capabilityKey), { capabilityKey, scopeCeiling: scope, state: 'granted' }]
      : entry.grants.filter((grant) => grant.capabilityKey !== capabilityKey);
    const capability = administration.capabilities.find((item) => item.key === capabilityKey);
    setWorking(next); setReview(null); setErrors([]);
    setNotice(capability ? policyIntentNotice(capability) : '');
  };

  const changeRoleGrant = (role: Parameters<typeof withRoleGrant>[1], capabilityKey: string, checked: boolean) => {
    if (!working) return;
    setWorking({ ...working, policy: withRoleGrant(working.policy, role, capabilityKey, checked) });
    setReview(null); setErrors([]);
    const capability = role.capabilities.find((item) => item.key === capabilityKey);
    setNotice(capability && capability.enforcement.status !== 'policyEnforced'
      ? 'Policy intent only: this change does not currently alter live access.' : '');
  };

  const run = async (action: () => Promise<void>) => {
    setBusy(true); setNotice('');
    try { await action(); } catch (cause) { setNotice(cause instanceof Error ? cause.message : 'The operation failed.'); }
    finally { setBusy(false); }
  };
  const save = () => run(async () => {
    if (!working || !administration.draft || !administration.draftMetadata) return;
    const result = await api.updateDraft(working.revisionId, administration.draftMetadata.editVersion, working.reason ?? '', working.policy);
    setErrors(result.errors ?? []);
    setNotice(result.valid ? 'Draft saved.' : 'Draft has validation errors.');
  });
  const discard = () => {
    if (!administration.draft) return;
    setWorking(cloneRevision(administration.draft));
    setErrors([]); setReview(null); setNotice('Unsaved changes discarded.');
  };
  const validate = () => run(async () => {
    if (dirty) { setNotice('Save the draft before validation.'); return; }
    if (!working || !administration.draftMetadata) return;
    const result = await api.validateDraft(working.revisionId, administration.draftMetadata.editVersion);
    setErrors(result.errors); setReview(result.valid ? { diff: result.diff, roleDiff: result.roleDiff ?? [], fingerprint: result.canonicalFingerprint } : null);
    setNotice(result.valid ? 'Draft passed authoritative server validation.' : 'Draft validation failed.');
  });
  const publish = () => run(async () => {
    if (!working || !administration.draftMetadata || !review) return;
    if (!confirm(`Publish revision ${working.revisionId}? This changes active authorization immediately.`)) return;
    await api.publishDraft(working.revisionId, administration.draftMetadata.editVersion, administration.policySet.activeRevisionId);
    setReview(null); setView('active'); setNotice('Policy revision published.');
  });

  return (
    <section className="card set-card permissions-admin">
      <div className="set-cardhead permissions-head">
        <div>
          <h2>Position Permissions</h2>
          <div className="subtitle">Review explicit Position-policy grants and where Compass currently enforces them.</div>
        </div>
        <div className="permissions-badges">
          <span className="pill active">Active · v{api.data.active.revisionNumber}</span>
          {api.data.draft && <span className="pill">Draft · v{api.data.draft.revisionNumber}</span>}
        </div>
      </div>

      <div className="set-subtabs">
        {(['active', 'draft', 'history'] as const).map((item) => <button key={item} className={`set-subtab${view === item ? ' active' : ''}`} onClick={() => setView(item)}>{item[0].toUpperCase() + item.slice(1)}</button>)}
      </div>

      {view === 'history' ? (
        <div className="permissions-history">
          {api.data.revisions.map((revision) => <div className="permissions-revision" key={revision.revisionId}>
            <b>{revision.revisionId}</b><span>{revision.state}</span><span>v{revision.revisionNumber}</span>
            <code>{revision.fingerprint ?? 'Draft — no fingerprint'}</code>
            <small>{revision.publishedAt
              ? `Published ${new Date(revision.publishedAt).toLocaleString()} by ${revision.publishedByUid}`
              : `Created ${new Date(revision.createdAt).toLocaleString()} by ${revision.createdByUid}`}</small>
          </div>)}
        </div>
      ) : view === 'draft' && !working ? (
        <div className="permissions-empty">
          <h3>No editable draft</h3><p>Create a durable draft from the active revision. The active policy will remain unchanged.</p>
          <button className="btn primary" disabled={busy} onClick={() => run(async () => { await api.createDraft(''); setView('draft'); })}>Create draft</button>
        </div>
      ) : (
        <>
          <div className="permissions-policy-summary">
            <div>
              <h3>{view === 'draft' ? `Draft permissions · v${shown.revisionNumber}` : 'Active permissions'}</h3>
              <p>{shown.publishedAt
                ? `Published ${new Date(shown.publishedAt).toLocaleString()}`
                : `Draft created ${new Date(shown.createdAt).toLocaleString()}`}</p>
            </div>
            {view === 'draft' && working && <label>Change reason<input value={working.reason ?? ''} maxLength={500} onChange={(event) => setWorking({ ...working, reason: event.target.value })} placeholder="Why is this policy changing?" /></label>}
          </div>
          <div className="permissions-concepts" aria-label="How Position permissions work">
            <div><b>Reports to</b><span>Organizational supervision only.</span></div>
            <div><b>Responsibility coverage</b><span>Direct and supervised organizational areas.</span></div>
            <div><b>Permission scope</b><span>The boundary applied to an explicit grant.</span></div>
            <div><b>Actual access</b><span>The result of the authoritative rule for the operation.</span></div>
          </div>
          <div className="permissions-note"><b>Reporting relationships do not grant access by themselves.</b> Policy grants create access only where Compass currently enforces that capability. Administrator authority, membership, ownership, participation, assignment, and other existing rules may also apply.</div>
          <div className="permissions-enforcement-legend" aria-label="Capability enforcement status">
            {(Object.keys(ENFORCEMENT_LABELS) as Array<keyof typeof ENFORCEMENT_LABELS>).map((status) => (
              <span key={status} className={`permissions-enforcement ${status}`}>{ENFORCEMENT_LABELS[status]}</span>
            ))}
          </div>
          {view === 'draft' && working && <div className="permissions-actions">
            <div><strong>Draft v{working.revisionNumber}</strong><span>{dirty ? 'Unsaved changes' : review ? 'Ready to publish' : 'Draft saved'}</span></div>
            <button className="btn" disabled={busy || !dirty} onClick={discard}>Discard Changes</button>
            <button className="btn" disabled={busy || !dirty} onClick={save}>Save Draft</button>
            <button className="btn" disabled={busy || dirty} onClick={validate}>Validate &amp; Review</button>
            <button className="btn primary" disabled={busy || dirty || !review || (review.diff.length === 0 && review.roleDiff.length === 0)} onClick={publish}>Publish</button>
          </div>}
          <div className="permissions-category-tabs" role="tablist" aria-label="Permission categories">
            {groups.map((category) => <button
              key={category}
              type="button"
              role="tab"
              aria-selected={selectedCategory === category}
              className={selectedCategory === category ? 'active' : ''}
              onClick={() => { setSelectedCategory(category); setCapabilitySearch(''); }}
            >{CATEGORY_LABELS[category] ?? category}</button>)}
          </div>
          <div className="permissions-filters">
            <input value={positionSearch} onChange={(event) => setPositionSearch(event.target.value)} placeholder="Search Positions" aria-label="Search Positions" />
            <input value={capabilitySearch} onChange={(event) => setCapabilitySearch(event.target.value)} placeholder="Search permissions in this area" aria-label="Search permissions in this area" />
          </div>
          <section className="permissions-group" role="tabpanel">
            <div className="permissions-group-head"><h3>{CATEGORY_LABELS[selectedCategory] ?? selectedCategory}</h3><span>{capabilities.length} permission{capabilities.length === 1 ? '' : 's'}</span></div>
            {selectedCategory === 'fos' && <div className="permissions-note">FOS permissions marked — can only be granted at council scope, so they are not available to district-level Positions. Scroll sideways to see all {capabilities.length} permissions; the Position column stays in view.</div>}
            {capabilities.length === 0 ? <p className="permissions-no-results">No permissions match this search.</p> :
              <div className="permissions-table-wrap"><table className="permissions-table"><thead><tr><th>Position</th>{capabilities.map((capability) => <th key={capability.key} title={`${capability.description} ${capability.enforcement.note}`}><span>{capability.label}</span><small className={`permissions-enforcement ${capability.enforcement.status}`}>{ENFORCEMENT_LABELS[capability.enforcement.status]}</small></th>)}</tr></thead>
                <tbody>{positions.map((position) => <tr key={position.positionId}><th><span>{position.displayName}{position.protected ? ' · 🔒' : ''}</span><small>{position.configured ? 'Configured' : 'Not currently configured'} · {position.assignmentCount} assignment{position.assignmentCount === 1 ? '' : 's'}</small></th>
                  {capabilities.map((capability) => {
                    const grant = grantsFor(position.positionId).find((item) => item.capabilityKey === capability.key);
                    const scopes = allowedScopes(position.organizationLevel, capability.scopes);
                    const editable = view === 'draft' && !!working && scopes.length > 0 && !position.protected;
                    return <td key={capability.key} className={grant ? 'granted' : ''}>
                      {scopes.length === 0 ? <span title="This capability has no valid scope for this Position level.">—</span> : <>
                        <input type="checkbox" aria-label={`${position.displayName}: ${capability.label}`} checked={!!grant} disabled={!editable} onChange={(event) => changeGrant(position.positionId, capability.key, event.target.checked, grant?.scopeCeiling ?? scopes[scopes.length - 1])} />
                        {grant && <select aria-label={`${position.displayName}: ${capability.label} scope`} title={grant.scopeCeiling === 'subcommittee' ? 'This explicit Action grant is limited to the Position’s direct and supervised responsibility coverage. Supervision does not create the permission.' : 'Permission scope'} value={grant.scopeCeiling} disabled={!editable} onChange={(event) => changeGrant(position.positionId, capability.key, true, event.target.value as PolicyScope)}>{scopes.map((scope) => <option key={scope} value={scope}>{permissionScopeLabel(capability, scope)}</option>)}</select>}
                      </>}
                    </td>;
                  })}
                </tr>)}</tbody></table></div>
            }
          </section>
          {selectedCategory === 'fos' && <RelationshipRoleGrants
            roles={administration.relationshipRoles ?? []}
            granted={(role) => roleGrantKeys(shown.policy, role)}
            editable={view === 'draft' && !!working}
            onChange={changeRoleGrant}
          />}
        </>
      )}

      {errors.length > 0 && <div className="permissions-errors"><b>Validation issues</b>{errors.map((error, index) => <div key={`${error.code}-${index}`}>{error.message}</div>)}</div>}
      {review && <div className="permissions-review"><h3>Review changes</h3>{review.diff.length === 0 && review.roleDiff.length === 0 ? <p>No policy changes.</p> : null}{review.roleDiff.map((item) => {
        const role = administration.relationshipRoles.find((candidate) => candidate.role === item.role);
        const capability = role?.capabilities.find((candidate) => candidate.key === item.capabilityKey);
        return <div key={`${item.role}-${item.capabilityKey}`}><b>{item.kind === 'added' ? '+' : '−'} {role?.label ?? 'Unknown role'} (on its own relationship only)</b> · {capability?.label ?? 'Unknown permission'}{capability && capability.enforcement.status !== 'policyEnforced' && <small className={`permissions-enforcement ${capability.enforcement.status}`}> Policy intent · {ENFORCEMENT_LABELS[capability.enforcement.status]}</small>}</div>;
      })}{review.diff.length === 0 ? null : review.diff.map((item) => {
        const position = administration.positions.find((candidate) => candidate.positionId === item.positionId)?.displayName ?? 'Unknown position';
        const capability = administration.capabilities.find((candidate) => candidate.key === item.capabilityKey)?.label ?? 'Unknown permission';
        const option = administration.capabilities.find((candidate) => candidate.key === item.capabilityKey);
        return <div key={`${item.positionId}-${item.capabilityKey}`}><b>{item.kind === 'added' ? '+' : item.kind === 'removed' ? '−' : '↔'} {position}</b> · {capability} {item.kind === 'scopeChanged' && <span>({item.previousScope} → {item.nextScope})</span>} {option && option.enforcement.status !== 'policyEnforced' && <small className={`permissions-enforcement ${option.enforcement.status}`}>Policy intent · {ENFORCEMENT_LABELS[option.enforcement.status]}</small>}</div>;
      })}</div>}
      {notice && <p className={errors.length ? 'err' : 'ok'}>{notice}</p>}
    </section>
  );
}
